import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

interface NeighborhoodData {
  neighborhood: string;
  city: string;
  count: number;
}

interface GroupedResult {
  grouped: Record<string, NeighborhoodData[]>;
  sortedCities: string[];
  postalCodes: string[];
  total: number;
}

function groupNeighborhoods(
  list: { city: string; neighborhood: string; count: number }[],
  postalCodes: string[] = [],
): GroupedResult {
  const grouped: Record<string, NeighborhoodData[]> = {};
  list.forEach((item) => {
    if (!grouped[item.city]) grouped[item.city] = [];
    grouped[item.city].push({
      neighborhood: item.neighborhood,
      city: item.city,
      count: item.count,
    });
  });

  Object.keys(grouped).forEach((city) => {
    grouped[city].sort((a, b) => b.count - a.count);
  });

  const sortedCities = Object.keys(grouped).sort((a, b) => {
    const aTotal = grouped[a].reduce((sum, n) => sum + n.count, 0);
    const bTotal = grouped[b].reduce((sum, n) => sum + n.count, 0);
    return bTotal - aTotal;
  });

  return { grouped, sortedCities, postalCodes, total: list.length };
}

/**
 * Lightweight: only neighborhoods (no postal codes).
 * Use this on pages that don't need the postal-code search index — the
 * postal codes array is ~100KB and was previously downloaded on every
 * homepage load.
 */
export function useNeighborhoods() {
  return useQuery({
    queryKey: ['neighborhoods-grouped'],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_neighborhoods_grouped' as any);
      if (error) throw error;
      const list = (data as { city: string; neighborhood: string; count: number }[]) || [];
      return groupNeighborhoods(list);
    },
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Full index including postal codes. Use only where postal-code search
 * suggestions are needed (e.g. SearchBar).
 */
export function useNeighborhoodsIndex(enabled: boolean = true) {
  return useQuery({
    queryKey: ['neighborhoods-index'],
    enabled,
    queryFn: async () => {
      // 24h localStorage cache — RPC payload is large (~100KB postal codes)
      const CACHE_KEY = 'bv:neighborhoods-index:v1';
      const TTL_MS = 24 * 60 * 60 * 1000;
      try {
        const raw = typeof window !== 'undefined' ? localStorage.getItem(CACHE_KEY) : null;
        if (raw) {
          const parsed = JSON.parse(raw);
          if (parsed && parsed.ts && Date.now() - parsed.ts < TTL_MS && parsed.data) {
            return groupNeighborhoods(parsed.data.neighborhoods || [], parsed.data.postalCodes || []);
          }
        }
      } catch { /* ignore cache errors */ }

      const { data, error } = await supabase.rpc('get_neighborhoods_index');
      if (error) throw error;
      const result = data as {
        neighborhoods: { city: string; neighborhood: string; count: number }[];
        postalCodes: string[];
      };
      try {
        if (typeof window !== 'undefined') {
          localStorage.setItem(CACHE_KEY, JSON.stringify({ ts: Date.now(), data: result }));
        }
      } catch { /* quota — ignore */ }
      return groupNeighborhoods(result.neighborhoods || [], result.postalCodes || []);
    },
    staleTime: 5 * 60 * 1000,
  });
}
